import React, { useContext, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import FrameworkView from './View/FrameworkView'
import { useTypedSelector } from 'src/State/RootReducer'
import { SystemDataProvider } from './SystemDataProvider'
import { UserGoogleAgreementProvider } from './UserGoogleAgreementProvider'
import { FarmProvider } from '../Farm'
import DynamicPowerPricesEnabledProvider from 'src/Libs/dynamic-power-prices/context/dynamic-power-prices-enabled-provider'
import InstallationOnlineStatusProvider from 'src/Libs/installations/context/installation-online-status-provider'
import Tokens from './Tokens'
import useOpenNotificationsModal from 'src/Libs/notifications/hooks/use-open-notifications-modal'
import useShopFrameworkLogin from 'src/Libs/shop/hooks/use-shop-framework-login'
import { useInitSystemDataStore } from 'src/Libs/system-data-handler/hooks'
import { useInitUserCompanyData } from 'src/Libs/user/hooks'
import { PageActionsProvider } from 'src/Libs/header/context'
import { useSetBrowserTabTitle } from './hooks'
import { AuthContext } from '@e3dc-react/shell/Libs/authentication'
import { ChartDateBarProvider } from 'src/Libs/chart-kit/shared/context/chart-date-bar-provider'
import { AppRatingContainer } from 'src/Libs/app-rating/components'
import { AddInstallationAddressAndCoordinate } from 'src/Libs/installation-details/components'
import { AppRatingProvider } from 'src/Libs/app-rating/provider'
import { SuspenseContentFallback } from '@e3dc-react/shell/Elements/Suspense'
import { ShopCartProvider } from 'src/Libs/shop/provider/shop-cart-provider'
import { REDIRECT_QUERY_PARAM, REDIRECT_URL_KEY } from 'src/Libs/navigation/consts'
import { DashboardTourProvider } from 'src/Libs/dashboard/guided-tour'

interface FrameworkProps {
  withContent?: boolean
  children?: React.ReactNode
}

/**
 * The framework of the app which provides multiple data to the app's content
 *
 * @param withContent load framework with it's content / child's
 * @returns
 */
const Framework: React.FC<FrameworkProps> = ({ withContent = true, children }) => {
  const { valid } = useContext(AuthContext)
  const navigate = useNavigate()
  const storeInitialised = useTypedSelector(state => state.storage.storeInitialised)
  const location = useLocation()

  /* hook to set browser title */
  useSetBrowserTabTitle()
  useOpenNotificationsModal(withContent)
  useShopFrameworkLogin()
  useInitSystemDataStore(withContent)
  useInitUserCompanyData()

  useEffect(() => {
    if (!valid && withContent) {
      /* if not root route, save redirect url in local storage */
      if (location.pathname !== '/') {
        localStorage.setItem(REDIRECT_URL_KEY, location.pathname)
      }

      if (location.search) {
        localStorage.setItem(REDIRECT_QUERY_PARAM, location.search)
      }

      navigate('/login')
    }
  }, [location.pathname, location.search, navigate, valid, withContent])

  if (!valid && withContent) return <SuspenseContentFallback />

  return (
    <FarmProvider>
      <SystemDataProvider>
        <UserGoogleAgreementProvider>
          <PageActionsProvider>
            <InstallationOnlineStatusProvider>
              <DynamicPowerPricesEnabledProvider>
                <ChartDateBarProvider>
                  <AppRatingProvider loggedIn={valid}>
                    <ShopCartProvider>
                      <FrameworkView>
                        <DashboardTourProvider>
                          {valid && withContent && <Tokens />}
                          {!withContent || storeInitialised ? children : <SuspenseContentFallback />}
                        </DashboardTourProvider>
                        <AppRatingContainer />
                        {valid && withContent && <AddInstallationAddressAndCoordinate />}
                      </FrameworkView>
                    </ShopCartProvider>
                  </AppRatingProvider>
                </ChartDateBarProvider>
              </DynamicPowerPricesEnabledProvider>
            </InstallationOnlineStatusProvider>
          </PageActionsProvider>
        </UserGoogleAgreementProvider>
      </SystemDataProvider>
    </FarmProvider>
  )
}

export default Framework
